import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { useDemand } from "lib/authorization/use-demand"
import { useTab } from "lib/@components/tabs"
import { TaskRowEntryWrapper } from "slot-definitions"
import { ScheduleIntroductionContentWrapper } from "routes/schedule/scheduleIntroduction"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import {
    SHARE_LINK_COMMENT_SHARE_PARAM,
    SHARE_LINK_COMMENTS_TAB,
    shouldShowShareLinkCommentsTab,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import { isEnabled } from "lib/@components/feature"
import { ShareLinkCommentsPanel } from "./share-link-comments-panel"
import { ShareLinkSelectedIntroCommentTarget } from "./share-link-selected-intro-comment-target"
import { ShareLinkSelectedTaskCommentTarget } from "./share-link-selected-task-comment-target"
import { useScheduleRightBarTab } from "./use-schedule-right-bar-tab"
import { FEATURE_FLAG_SHARE_LINK_COMMENTS } from "./share-link-comments-feature-flag"

export function ShareLinkCommentsTabRegistrar() {
    const { isShare, schedule } = useBoundContext()
    const [shareLinkId] = useSearchParam("share", null)
    const [commentShareLinkId] = useSearchParam(SHARE_LINK_COMMENT_SHARE_PARAM, null)
    const rightBarTab = useScheduleRightBarTab()
    const commentsLinkId = shareLinkId ?? commentShareLinkId
    const shareLink = getSharingLink.useResults(commentsLinkId)
    const isRegimeEditor = useDemand("manageRegime")
    // Authoring (new threads + selection targets) needs both a comment-enabled share link in context
    // AND the share-link-comments feature demand. Recipients inherit that demand from the link owner's
    // client auth bundle (ConfigureShareLinkDemands), so the gate follows the feature/auth bundle: when
    // it is disabled the demand is absent and the recipient surface closes rather than bleeding through
    // the link's persisted allowComments (FIQ-1668). The server also re-checks on every write.
    const canCreate =
        shouldShowShareLinkCommentsTab({
            isShare: !!isShare || !!shareLinkId,
            shareLinkId: commentsLinkId,
            shareLink,
        }) && isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)
    // Internal reviewers get the Comments tab so they can read/triage a schedule's comments across every
    // share link. This is gated on regime-edit permission (matching the server, which scopes the
    // regime query to regime editors) — NOT merely on lacking the `$sharing` demand, because a
    // demandless registered recipient also lacks `$sharing` yet must NOT be treated as an internal
    // reviewer. Recipients reach the tab only through `canCreate` (their link's allowComments). The
    // internal path also requires the feature flag (the feature's rollout surface) and no `?share=`
    // link in the URL (an internal reviewer browses the regime without one).
    const isInternal = !isShare && !shareLinkId && isRegimeEditor && isEnabled(FEATURE_FLAG_SHARE_LINK_COMMENTS)
    const canShow = !!schedule?.id && (isInternal || canCreate)
    // Share-link recipients get the in-document scroll/highlight targets via `canCreate` (their link is
    // in context). Internal reviewers have no share-link id in the URL, so they previously never got
    // them — which is why clicking a comment did nothing in the internal "Open" and draft/edit views.
    // Mount them whenever an internal reviewer is actually viewing the Comments tab.
    const canHighlight = canCreate || (isInternal && (!!commentsLinkId || rightBarTab === SHARE_LINK_COMMENTS_TAB))
    const title = getTitle(canShow)
    const content = getContent(canShow)

    TaskRowEntryWrapper.usePlug(canHighlight && <ShareLinkSelectedTaskCommentTarget priority={130} />, [canHighlight])
    ScheduleIntroductionContentWrapper.usePlug(canHighlight && <ShareLinkSelectedIntroCommentTarget priority={130} />, [
        canHighlight,
    ])

    useTab({
        tab: "ScheduleRightBar",
        id: SHARE_LINK_COMMENTS_TAB,
        title,
        content,
    })

    return null
}

function getTitle(canShow) {
    if (canShow) return SHARE_LINK_COMMENTS_TAB
    return false
}

function getContent(canShow) {
    if (canShow) return <ShareLinkCommentsPanel priority={25} />
    return null
}
